// src/components/chat/ChatMessage.tsx
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MicIcon from "@mui/icons-material/Mic";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { alpha, Box, IconButton, Paper, Typography, useTheme, Theme, Chip } from "@mui/material";
import React, { useState, useRef } from "react";
import { athenaColors } from "../../theme/palette";
import { useTranslation } from "../../i18n";

// Helper to get content text color from theme
const getContentTextColor = (theme: Theme, variant: "primary" | "secondary" | "muted" = "primary") => {
  return theme.custom?.contentText?.[variant] || theme.palette.text[variant === "muted" ? "disabled" : variant];
};

// Speaker prefix patterns from backend
const SPEAKER_PATTERNS = {
  doctor: /^(Doctor|DOCTOR|Dr\.?|Médico|MEDICO):\s*/i,
  patient: /^(Patient|PATIENT|Paciente|PACIENTE):\s*/i,
};

// Render transcription with styled speaker labels (theme-compatible colors)
const renderTranscriptionWithSpeakers = (text: string, theme: Theme, t: (key: string, fallback: string) => string): React.ReactNode => {
  const lines = text.split("\n").filter((line) => line.trim());
  
  // Speaker colors — branded Athena palette
  // Doctor: Royal Blue — professional, authoritative
  // Patient: Soft Purple — warm, human
  const doctorColor = athenaColors.royalBlue;
  const patientColor = athenaColors.softPurple;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
      {lines.map((line, index) => {
        const trimmedLine = line.trim();
        let speaker: "doctor" | "patient" | null = null;
        let content = trimmedLine;

        // Check for speaker prefix
        if (SPEAKER_PATTERNS.doctor.test(trimmedLine)) {
          speaker = "doctor";
          content = trimmedLine.replace(SPEAKER_PATTERNS.doctor, "");
        } else if (SPEAKER_PATTERNS.patient.test(trimmedLine)) {
          speaker = "patient";
          content = trimmedLine.replace(SPEAKER_PATTERNS.patient, "");
        }

        if (speaker) {
          const isDoctor = speaker === "doctor";
          const speakerColor = isDoctor ? doctorColor : patientColor;

          return (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                p: 0.5,
                borderRadius: 1,
                backgroundColor: alpha(speakerColor, 0.07),
                borderLeft: `2px solid ${speakerColor}`,
              }}
            >
              <Chip
                icon={isDoctor ? <LocalHospitalIcon sx={{ fontSize: 12 }} /> : <PersonIcon sx={{ fontSize: 12 }} />}
                label={isDoctor ? t("chat.doctor_label", "Dr") : t("chat.patient_label", "Pt")}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 600,
                  backgroundColor: speakerColor,
                  color: theme.palette.common.white,
                  "& .MuiChip-icon": { color: theme.palette.common.white, marginLeft: "4px" },
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
              <Typography
                variant="body2"
                sx={{ lineHeight: 1.4, flex: 1 }}
              >
                {content}
              </Typography>
            </Box>
          );
        }

        // Regular line without speaker
        return (
          <Typography
            key={index}
            variant="body2"
            sx={{ lineHeight: 1.4, pl: 0.5, color: getContentTextColor(theme, "primary") }}
          >
            {trimmedLine}
          </Typography>
        );
      })}
    </Box>
  );
};

export type ChatMessageType = {
  id: string;
  type: "text" | "document" | "photo" | "voice" | "video";
  content: string; // text or file URL
  timestamp: Date;
  sender?: string; // Username or full name of the sender
  metadata?: {
    fileName?: string;
    fileSize?: number;
    duration?: number; // for audio/video in seconds
    transcription?: string; // for voice notes
    serverId?: string | number; // server document ID for reference
  };
};

interface ChatMessageProps {
  message: ChatMessageType;
  onDocumentClick?: (url: string) => void;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  onDocumentClick,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isDark = theme.palette.mode === "dark";
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleDocumentClick = () => {
    if (onDocumentClick && message.type !== "text") {
      onDocumentClick(message.content);
    }
  };

  // Format timestamp for display
  const formatTimestamp = (date: Date): string => {
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const timeStr = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    
    if (isToday) {
      return timeStr;
    }
    return `${date.toLocaleDateString([], { month: "short", day: "numeric" })} ${timeStr}`;
  };

  // ─── Unified card style (matches NeumorphicCard dark variant) ───
  // All messages use the same background — no user/system distinction
  const darkBaseOpacity = isDark ? 0.1 : 0.06;
  const cardBg = `linear-gradient(180deg, ${alpha(
    theme.palette.primary.dark,
    darkBaseOpacity
  )} 0%, ${alpha(theme.palette.primary.dark, darkBaseOpacity)} 100%)`;
  const cardBorder = isDark
    ? alpha(theme.palette.secondary.main, 0.55)
    : alpha(theme.palette.primary.dark, 0.35);
  const cardTextColor =
    theme.custom?.contentText?.primary || theme.palette.text.primary;

  const cardSx = {
    background: cardBg,
    backdropFilter: "blur(8px)",
    border: `1px solid ${cardBorder}`,
    borderRadius: 2,
    color: cardTextColor,
    maxWidth: "85%",
    wordBreak: "break-word" as const,
  };

  // Message header with sender and timestamp
  const MessageHeader = () => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
      {message.sender && (
        <Typography variant="caption" fontWeight={600} sx={{ color: getContentTextColor(theme, "secondary") }}>
          {message.sender}
        </Typography>
      )}
      {message.sender && (
        <Typography variant="caption" sx={{ color: getContentTextColor(theme, "muted") }}>•</Typography>
      )}
      <Typography variant="caption" sx={{ color: getContentTextColor(theme, "muted") }}>
        {formatTimestamp(message.timestamp)}
      </Typography>
    </Box>
  );

  // Text message color — royalBlue on gradient, periwinkle on classic
  const textMessageColor =
    theme.custom?.contentText?.secondary || theme.palette.text.secondary;

  // Compact text message
  if (message.type === "text") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <MessageHeader />
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 0.75,
            ...cardSx,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.4, color: textMessageColor }}>
            {message.content}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // Document message
  if (message.type === "document") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <MessageHeader />
        <Paper
          onClick={handleDocumentClick}
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            ...cardSx,
            cursor: "pointer",
            transition: "border-color 0.15s",
            "&:hover": {
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <InsertDriveFileIcon sx={{ fontSize: 20, color: getContentTextColor(theme, "secondary") }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={500} noWrap sx={{ color: cardTextColor }}>
                {message.metadata?.fileName || t("chat.document_fallback", "Document")}
              </Typography>
              {message.metadata?.fileSize && (
                <Typography variant="caption" sx={{ color: getContentTextColor(theme, "secondary") }}>
                  {(message.metadata.fileSize / 1024).toFixed(1)} KB
                </Typography>
              )}
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Photo message
  if (message.type === "photo") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <MessageHeader />
        <Paper
          onClick={handleDocumentClick}
          elevation={0}
          sx={{
            p: 0.5,
            ...cardSx,
            maxWidth: "200px",
            cursor: "pointer",
            overflow: "hidden",
            transition: "border-color 0.15s",
            "&:hover": {
              borderColor: alpha(theme.palette.primary.main, 0.5),
            },
          }}
        >
          <img
            src={message.content}
            alt={message.metadata?.fileName || "Photo"}
            style={{
              width: "100%",
              borderRadius: 8,
              display: "block",
            }}
          />
        </Paper>
      </Box>
    );
  }

  // Voice message - with both audio and transcription
  if (message.type === "voice") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <MessageHeader />
        <Paper
          elevation={0}
          sx={{
            px: 1.5,
            py: 1,
            ...cardSx,
          }}
        >
          {/* Audio control row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              size="small"
              onClick={handlePlayPause}
              sx={{
                backgroundColor: theme.palette.primary.dark,
                color: theme.palette.common.white,
                width: 28,
                height: 28,
                "&:hover": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            >
              {isPlaying ? (
                <PauseIcon sx={{ fontSize: 16 }} />
              ) : (
                <PlayArrowIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <MicIcon sx={{ fontSize: 14, color: getContentTextColor(theme, "secondary") }} />
              <Typography variant="caption" sx={{ color: getContentTextColor(theme, "secondary") }}>
                {t("chat.voice_note", "Voice Note")}
                {message.metadata?.duration
                  ? ` • ${formatDuration(message.metadata.duration)}`
                  : ""}
              </Typography>
            </Box>
          </Box>

          {/* Transcription with speaker styling */}
          {message.metadata?.transcription && (
            <Box
              sx={{
                mt: 1,
                pt: 1,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {renderTranscriptionWithSpeakers(message.metadata.transcription, theme, t)}
            </Box>
          )}

          <audio
            ref={audioRef}
            src={message.content}
            onEnded={() => setIsPlaying(false)}
            style={{ display: "none" }}
          />
        </Paper>
      </Box>
    );
  }

  // Video message
  if (message.type === "video") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <MessageHeader />
        <Paper
          elevation={0}
          sx={{
            p: 0.5,
            ...cardSx,
            maxWidth: "300px",  // override cardSx.maxWidth for video
            overflow: "hidden",
          }}
        >
          <video
            controls
            src={message.content}
            style={{
              width: "100%",
              borderRadius: 8,
              display: "block",
            }}
          />
        </Paper>
      </Box>
    );
  }

  return null;
};

export default ChatMessage;
